import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";

export default function SuppliersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/suppliers/${editing.id}`, data);
      else await api.post("/suppliers", data);
      toast.success("Supplier saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error("Could not save"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete supplier?")) return;
    await api.delete(`/suppliers/${id}`); load();
  };

  return (
    <div className="px-6 lg:px-14 py-10 lg:py-14 max-w-6xl" data-testid="suppliers-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline">Studio · Trade</div>
          <h1 className="font-serif font-light mt-3" style={{ fontSize: "44px", lineHeight: 1.05 }}>Suppliers</h1>
          <p className="text-muted-foreground mt-3">Your trade relationships — stone, timber, cabinetry, hardware and specialist services.</p>
        </div>
        <button data-testid="btn-add-supplier" onClick={() => { setEditing({}); setOpen(true); }} className="btn-primary">
          <Plus className="w-4 h-4" strokeWidth={1.5}/> Add supplier
        </button>
      </div>

      {items.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <div className="font-serif text-2xl mb-2">No suppliers yet</div>
          <button onClick={() => { setEditing({}); setOpen(true); }} className="btn-accent mx-auto mt-4">
            <Plus className="w-4 h-4" strokeWidth={1.5}/> Add first supplier
          </button>
        </div>
      ) : (
        <div className="luxe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: "#F4F2EC" }}>
                <th className="text-left px-6 py-4 overline text-[10px]">Name</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Email</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden md:table-cell">Phone</th>
                <th className="text-left px-6 py-4 overline text-[10px] hidden lg:table-cell">Terms</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.website}</div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell text-muted-foreground">{s.email}</td>
                  <td className="px-6 py-4 hidden md:table-cell text-muted-foreground">{s.phone}</td>
                  <td className="px-6 py-4 hidden lg:table-cell text-muted-foreground">{s.payment_terms}</td>
                  <td className="px-6 py-4 text-right">
                    <button data-testid={`btn-edit-sup-${s.id}`} onClick={() => { setEditing(s); setOpen(true); }} className="p-2"><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                    <button data-testid={`btn-del-sup-${s.id}`} onClick={() => remove(s.id)} className="p-2 text-[color:hsl(var(--destructive))]"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <SupplierForm supplier={editing} onClose={() => { setOpen(false); setEditing(null); }} onSave={save} />}
    </div>
  );
}

function SupplierForm({ supplier, onClose, onSave }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", website: "", address: "", account_number: "",
                              freight_notes: "", payment_terms: "Net 30", notes: "", active: true, ...supplier });
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-auto" data-testid="supplier-form-modal">
      <div className="bg-card max-w-2xl w-full p-8 border border-border my-8">
        <div className="flex justify-between mb-6">
          <div>
            <div className="overline">Supplier</div>
            <div className="font-serif text-3xl mt-2">{supplier?.id ? "Edit supplier" : "Add supplier"}</div>
          </div>
          <button onClick={onClose}><X strokeWidth={1.5}/></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name *" testid="input-sup-name" value={f.name} onChange={s("name")}/>
          <Field label="Website" testid="input-sup-web" value={f.website} onChange={s("website")}/>
          <Field label="Email" testid="input-sup-email" value={f.email} onChange={s("email")}/>
          <Field label="Phone" testid="input-sup-phone" value={f.phone} onChange={s("phone")}/>
          <Field label="Account #" testid="input-sup-acct" value={f.account_number} onChange={s("account_number")}/>
          <Field label="Payment terms" testid="input-sup-terms" value={f.payment_terms} onChange={s("payment_terms")}/>
        </div>
        <div className="mt-4">
          <label className="overline block mb-2">Address</label>
          <input className="luxe-input" data-testid="input-sup-addr" value={f.address} onChange={s("address")}/>
        </div>
        <div className="mt-4">
          <label className="overline block mb-2">Freight notes</label>
          <input className="luxe-input" data-testid="input-sup-freight" value={f.freight_notes} onChange={s("freight_notes")}/>
        </div>
        <div className="mt-4">
          <label className="overline block mb-2">Notes</label>
          <textarea rows="3" className="luxe-input" data-testid="input-sup-notes" value={f.notes} onChange={s("notes")}/>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button data-testid="btn-save-supplier" onClick={() => onSave(f)} className="btn-primary">Save supplier</button>
        </div>
      </div>
    </div>
  );
}
function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <input className="luxe-input" data-testid={testid} {...rest}/>
    </div>
  );
}
